import {Checkbox, DatePicker, Form, Input} from 'antd';
import {FormInstance} from 'antd/es/form/Form';
import React, {FC} from 'react';

import {EComparisonType} from 'components/form/hooks/use-form-watcher/use-form-watcher-constants';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {DATE_DMY} from 'shared/constants/date-format';

import {
    INVEST_PROJECT_STATUS_CODE_REQUIRED, INVEST_PROJECT_STATUS_IMPLEMENTED_CODE,
    INVEST_PROJECT_STATUS_PLANNING_CODE, BINARY_VALUES,
} from '../../model/project-investment-info-item-modal.constants';
import {IProjectInfluenceItemForm} from '../../model/project-investment-info-item-modal.types';
import {fourDigitRule, requiredInputRule, lengthValidationRule, numberLengthValidationRule} from '../../utils';
import {MainInfoBenefitsList} from './main-info-benefits-list';
import {MainInfoTerritoriesList} from './main-info-territories-list';

interface IMainInfoTabProps {
    form: FormInstance<IProjectInfluenceItemForm>;
}

export const MainInfoTab: FC<IMainInfoTabProps> = ({form}: IMainInfoTabProps) => {
    const handleInternationalChange = (value: string) => {
        if (value === BINARY_VALUES.NO) {
            const current = form.getFieldValue('territories');
            if (!current || current.length === 0) {
                form.setFieldsValue({territories: [{}]});
            }
        } else {
            form.setFieldsValue({territories: []});
        }
    };

    const handleHasBenefitChange = (value: string) => {
        if (value === BINARY_VALUES.YES) {
            const current = form.getFieldValue('benefits');
            if (!current || current.length === 0) {
                form.setFieldsValue({benefits: [{}]});
            }
        } else {
            form.setFieldsValue({benefits: []});
        }
    };

    return (
        <div className="form-flex">
            <div className="form-flex-row">
                <Form.Item
                    name={['projectInfo', 'active']}
                    valuePropName="checked"
                >
                    <Checkbox >
                        Признак активности инвестиционного проекта
                    </Checkbox>
                </Form.Item>
                <Form.Item
                    name={['projectInfo', 'investProjectId']}
                    label="Идентификационный код инвестиционного проекта"
                    rules={[requiredInputRule()]}
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={['projectInfo', 'investProjectName']}
                    label="Полное наименование инвестиционного проекта"
                    rules={[requiredInputRule()]}
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name={['projectInfo', 'investProjectShortName']}
                    label="Краткое наименование инвестиционного проекта"
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={['projectInfo', 'investProjectStatus']}
                    label="Статус инвестиционного проекта"
                    rules={[requiredInputRule()]}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=invest_project_status',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevInvestStatus = prevValues.projectInfo?.investProjectStatus;
                        const currentInvestStatus = currentValues.projectInfo?.investProjectStatus;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const investStatus = getFieldValue(['projectInfo', 'investProjectStatus']);
                        const isActive = INVEST_PROJECT_STATUS_CODE_REQUIRED.includes(investStatus);

                        return (
                            <Form.Item
                                name={['projectInfo', 'investProjectStatusDescription']}
                                label="Пояснение к статусу инвестиционного проекта"
                                rules={isActive ? [requiredInputRule()] : []}
                            >
                                <Input.TextArea
                                    placeholder="Введите значение"
                                    autoComplete="off"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="investProjectCountry"
                    label="Местонахождение: Код страны по ОКСМ"
                    rules={[
                        requiredInputRule(),
                        numberLengthValidationRule({comparisonOperator: EComparisonType.LESS, value: 3}),
                    ]}
                >
                    <Input type="number" placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name="okvedCode"
                    label="Код ОКВЭД"
                    rules={[
                        requiredInputRule(),
                        lengthValidationRule({comparisonOperator: EComparisonType.GREATER, value: 2}),
                        lengthValidationRule({comparisonOperator: EComparisonType.LESS, value: 8}),
                    ]}
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="activityDirection"
                    label="Направление деятельности"
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name="investProjectTarget"
                    label="Цель реализации инвестиционного проекта"
                    rules={[requiredInputRule()]}
                >
                    <Input.TextArea placeholder="Введите значение" autoComplete="off" />
                </Form.Item>

                <Form.Item
                    name="investBaseDocumentType"
                    label="Тип документа-основания"
                    rules={[requiredInputRule()]}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=invest_base_document_type',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevInvestStatus = prevValues.investBaseDocumentType;
                        const currentInvestStatus = currentValues.investBaseDocumentType;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const investBaseDocumentType = getFieldValue(['investBaseDocumentType']);
                        const isActive = INVEST_PROJECT_STATUS_CODE_REQUIRED.includes(investBaseDocumentType);

                        return (
                            <Form.Item
                                name="investBaseDocumentDescription"
                                label="Наименование документа-основания"
                                rules={[
                                    (isActive ? requiredInputRule() : {})
                                ]}
                            >
                                <Input disabled={!isActive} placeholder="Введите значение" autoComplete="off" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item
                    name="investBaseDocumentDate"
                    label="Дата документа-основания"
                    rules={[requiredInputRule()]}
                >
                    <DatePicker
                        className="date-picker"
                        placeholder="Введите значение"
                        format={DATE_DMY}
                    />
                </Form.Item>
                <Form.Item
                    name="investBaseDocumentNumber"
                    label="Номер документа основания"
                    rules={[requiredInputRule()]}
                >
                    <Input placeholder="Введите номер" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="startPlanYear"
                    label="Плановый год начала реализации"
                    rules={[
                        requiredInputRule(),
                        fourDigitRule()
                    ]}
                >
                    <Input type="number" placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevInvestStatus = prevValues.projectInfo?.investProjectStatus;
                        const currentInvestStatus = currentValues.projectInfo?.investProjectStatus;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const investStatus = getFieldValue(['projectInfo', 'investProjectStatus']);
                        const isActive = investStatus && investStatus !== INVEST_PROJECT_STATUS_PLANNING_CODE;

                        return (
                            <Form.Item
                                name="startFactYear"
                                label="Фактический год начала реализации"
                                rules={
                                    isActive ? [
                                        requiredInputRule(),
                                        fourDigitRule()
                                    ] : []
                                }
                            >
                                <Input
                                    type="number"
                                    disabled={!isActive}
                                    placeholder="Введите значение"
                                    autoComplete="off"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item
                    name="operationPlanYear"
                    label="Плановый год ввода в эксплуатацию"
                    rules={[
                        requiredInputRule(),
                        fourDigitRule()
                    ]}
                >
                    <Input type="number" placeholder="Введите значение" autoComplete="off"/>
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevInvestStatus = prevValues.projectInfo?.investProjectStatus;
                        const currentInvestStatus = currentValues.projectInfo?.investProjectStatus;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const investStatus = getFieldValue(['projectInfo', 'investProjectStatus']);
                        const isActive = investStatus === INVEST_PROJECT_STATUS_IMPLEMENTED_CODE;

                        return (
                            <Form.Item
                                name="operationFactYear"
                                label="Фактический год ввода в эксплуатацию"
                                rules={
                                    isActive ? [
                                        requiredInputRule(),
                                        fourDigitRule()
                                    ] : []
                                }
                            >
                                <Input
                                    disabled={!isActive}
                                    type="number"
                                    placeholder="Введите значение"
                                    autoComplete="off"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name="isInternational"
                    label='Признак "Международный проект"'
                    rules={[requiredInputRule()]}
                >
                    <CustomSelect
                        style={{width:'240px'}}
                        onChange={handleInternationalChange}
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=yes_no_investment_project',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
            </div>

            <Form.Item
                shouldUpdate={(prev, curr) => prev.isInternational !== curr.isInternational}
                noStyle
            >
                {({getFieldValue}) => (
                    getFieldValue('isInternational') === BINARY_VALUES.NO
                        ? <MainInfoTerritoriesList />
                        : null
                )}
            </Form.Item>

            <div className="form-flex-row">
                <Form.Item
                    name="hasBenefit"
                    label="При реализации инвестиционного проекта применяется государственная поддержка"
                    rules={[requiredInputRule()]}
                >
                    <CustomSelect
                        onChange={handleHasBenefitChange}
                        style={{width:'240px'}}
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=yes_no_investment_project',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
            </div>

            <Form.Item
                shouldUpdate={(prev, curr) => prev.hasBenefit !== curr.hasBenefit}
                noStyle
            >
                {({getFieldValue}) => (
                    getFieldValue('hasBenefit') === BINARY_VALUES.YES
                        ? <MainInfoBenefitsList />
                        : null
                )}
            </Form.Item>
        </div>
    );
};
